/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import {
  ActionType,
  ANONYMOUS_ID,
  RouteHelper as CommonRouteHelper,
  IAppState,
  ISettingsMenuItemComponentModel,
  IUserInfoModel,
  ThemeContext,
  useConfigurationSelector,
  useIsLoggedIn,
  UserStore,
  useSelector,
  useSettingsMenuConfiguration,
} from "@bms/common";
import cx from "classnames";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { LogOutModal } from "components";
import { Button } from "components/Button";
import { useModal } from "context/modal";
import { RouteHelper } from "helpers";
import { useValuePulseOnIncrease } from "hooks/useValuePulseOnIncrease";
import avatarPlaceholder from "resources/avatar-placeholder.png";
import { ReactComponent as CryptoCoinIcon } from "resources/cryptoCoin.svg";
import ChevronDown from "resources/icons/chevron-down.svg";

import { UserAuthActions } from "./UserAuthActions";

import "./HeaderUserInfo.scss";

interface IHeaderUserInfoProps {
  user?: IUserInfoModel;
  isMobile?: boolean;
}

export const HeaderUserInfo = ({
  user,
  isMobile = false,
}: IHeaderUserInfoProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const settingsMenuScreen = useSettingsMenuConfiguration();
  const isLoggedIn = useIsLoggedIn();
  const [showList, setShowList] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const { setModal } = useModal();

  const onOpenLogoutModal = useCallback(
    () => setModal({ content: <LogOutModal /> }),
    [setModal],
  );

  const wallet = useSelector((state: IAppState) => state.user.wallet);
  const { themeProvider } = useContext(ThemeContext);
  const fontColor = themeProvider.getColor("AppPrimaryColor");

  const userWallet = wallet
    ? wallet.Data?.find((el) => el.CurrencyCode === "CC")?.Amount
    : 0;

  const isWalletPulsing = useValuePulseOnIncrease(userWallet);

  const configuration = useConfigurationSelector();

  const showBetterCoinWallet = configuration?.System?.CryptoCoin?.Enabled;

  useEffect(() => {
    if (isLoggedIn && showBetterCoinWallet) {
      dispatch(UserStore.Actions.getUserWallet());
    }
  }, [dispatch, isLoggedIn, showBetterCoinWallet]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, []);

  const handleClick = useCallback(
    (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        if (event.target instanceof SVGElement) {
          if (event.target.className.baseVal.includes("Button")) {
            return;
          }
        } else if (event.target instanceof Element) {
          if (event.target.className?.includes("Button")) {
            return;
          }
        }
        setShowList(false);
      }
    },
    [wrapperRef],
  );

  const handleToggleList = () => {
    setShowList((state) => !state);
  };

  useEffect(() => {
    if (showList) {
      const firstItem =
        listRef.current?.querySelector<HTMLButtonElement>("button");
      firstItem?.focus();
    }
  }, [showList]);

  const handleTriggerKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape" && showList) {
        e.preventDefault();
        setShowList(false);
        return;
      }
    },
    [showList],
  );

  const handleListKeyDown = useCallback((e: React.KeyboardEvent) => {
    const items =
      listRef.current?.querySelectorAll<HTMLButtonElement>("button");
    if (!items || items.length === 0) return;

    const currentIndex = Array.from(items).findIndex(
      (item) => item === document.activeElement,
    );

    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        const nextIndex = (currentIndex + 1) % items.length;
        items[nextIndex].focus();
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        const prevIndex = (currentIndex - 1 + items.length) % items.length;
        items[prevIndex].focus();
        break;
      }
      case "Home": {
        e.preventDefault();
        items[0].focus();
        break;
      }
      case "End": {
        e.preventDefault();
        items[items.length - 1].focus();
        break;
      }
      case "Escape": {
        e.preventDefault();
        setShowList(false);
        triggerRef.current?.focus();
        break;
      }
      case "Tab": {
        setShowList(false);
        break;
      }
    }
  }, []);

  const onMenuItemclick = async (
    component: ISettingsMenuItemComponentModel,
  ) => {
    await RouteHelper.performAction(component.Action);
    setShowList(false);
  };

  const renderProfileItems = useMemo(() => {
    return (
      <>
        {settingsMenuScreen?.Components?.filter(
          (component: ISettingsMenuItemComponentModel) =>
            component.IsVisible && component.Action,
        ).map((component: ISettingsMenuItemComponentModel) => {
          let title = t(
            `${component.TitleTranslationKey}`,
            component.Title ?? "",
          );

          if (component.Action?.ActionType === ActionType.SignInOut) {
            title = t(isLoggedIn ? "COMMON__LOGOUT" : "COMMON__SIGN_IN");
          }

          return (
            <Button
              type="button"
              buttonClassName="HeaderUserInfo__ListItem"
              key={component.Id}
              onClick={() => onMenuItemclick(component)}
              role="menuitem"
              tabIndex={-1}
            >
              {title}
            </Button>
          );
        })}
        <Button
          type="button"
          buttonClassName="HeaderUserInfo__ListItem"
          key="logout"
          onClick={onOpenLogoutModal}
          role="menuitem"
          tabIndex={-1}
        >
          {t(isLoggedIn ? "COMMON__LOGOUT" : "COMMON__SIGN_IN")}
        </Button>
      </>
    );
  }, [settingsMenuScreen]);

  const getInitials = (fullName: string | undefined) => {
    if (!fullName) {
      return "";
    }
    const allNames = fullName.trim().split(" ");
    const initials = allNames.reduce((prevValue, currValue, index) => {
      if (index === 0 || index === allNames.length - 1) {
        prevValue = `${prevValue}${currValue.charAt(0).toUpperCase()}`;
      }
      return prevValue;
    }, "");
    return initials;
  };

  if (user?.Id === ANONYMOUS_ID || !user) {
    return <UserAuthActions />;
  }

  if (isMobile) {
    return (
      <button
        type="button"
        className="button-reset HeaderUserInfo"
        onClick={() => CommonRouteHelper.goToSettings()}
      >
        <div className="HeaderUserInfo__AvatarContainer">
          <img
            className="HeaderUserInfo__Avatar"
            alt="user-avatar"
            src={user?.AvatarUrl || avatarPlaceholder}
          />
        </div>
      </button>
    );
  }

  return (
    <div className="HeaderUserInfo" ref={wrapperRef}>
      {showBetterCoinWallet && (
        <>
          <CryptoCoinIcon
            aria-hidden="true"
            className={cx("HeaderUserInfo__icon-wallet", {
              "HeaderUserInfo__icon-wallet--pulse": isWalletPulsing,
            })}
            focusable="false"
          />
          <div
            className={cx("HeaderUserInfo__icon-wallet-text", {
              "HeaderUserInfo__icon-wallet-text--pulse": isWalletPulsing,
            })}
            style={{ color: fontColor }}
          >
            {userWallet}
          </div>
        </>
      )}

      <button
        type="button"
        className="button-reset HeaderUserInfo__Content"
        ref={triggerRef}
        onClick={handleToggleList}
        onKeyDown={handleTriggerKeyDown}
        aria-label={t("USER_MENU")}
        aria-haspopup="true"
        aria-expanded={showList}
      >
        <div className="HeaderUserInfo__AvatarContainer">
          <img
            className="HeaderUserInfo__Avatar"
            alt="user-avatar"
            src={user?.AvatarUrl || avatarPlaceholder}
          />
        </div>
        <div className="HeaderUserInfo__Name">
          {getInitials(user?.FullName)}
        </div>
        <div
          className="HeaderUserInfo__Icon"
          style={{ transform: `rotate(${showList ? 180 : 0}deg)` }}
        >
          <ChevronDown aria-hidden="true" />
        </div>
      </button>
      {showList && (
        <div
          className="HeaderUserInfo__List"
          ref={listRef}
          role="menu"
          aria-label={t("USER_MENU")}
          onKeyDown={handleListKeyDown}
          tabIndex={-1}
        >
          {renderProfileItems}
        </div>
      )}
    </div>
  );
};
